/**
 * Temperature-log workflow data — REAL server backend.
 *
 * The chef gets **one CHEF_SUBMIT_TEMP_LOG task per day** that covers
 * EVERY location they deliver to. Opening it lands on a workflow page
 * where each location is a step; for the selected location the chef
 * records a Kitchen °F and a Drop-off °F for every deliverable, grouped
 * like the orders page / print sheet: **Meal period → Deliverable**.
 * Each location is confirmed independently.
 *
 * Server-side the generator materializes one BlockingTask per SHIPMENT
 * (one per location+mealtime+day). The seams here keep the day-grouped
 * UX on top of that:
 *
 *   - `useChefTempLogTasks`   — `/api/temp-log/days` → one synthetic
 *       `MyTaskRow` per open day (id `temp-log-day-YYYY-MM-DD`, same as
 *       always, so routing + `temp-log-days.ts` helpers are unchanged).
 *   - `useTempLogDayContext`  — `/api/temp-log/day-context` → locations
 *       with meal-grouped deliverables, per-location `confirmed` state,
 *       and previously-saved readings read back from the server.
 *   - Confirming a location → `useConfirmTempLogLocationMutation`
 *       (`api/temp-log.ts`) — ONE transactional server call that writes
 *       the durable rows and completes that location's underlying tasks.
 *
 * Save-for-later DRAFTS stay in the day-keyed localStorage store
 * (`stores/chefs/temp-log-tasks.ts`); only confirms hit the server.
 */

import { useMemo } from 'react'

// Relative imports (not the `@/` alias) so bun test can import the
// pure projection helpers directly — mirrors `temp-log-progress.ts`.
import { apiFetch } from '../../api/http'
import type { MyTaskRow } from '../../api/tasks'
import {
  type TempLogContextLocationWire,
  type TempLogDaySummaryWire,
  useTempLogDayContextQuery,
  useTempLogDaysQuery,
} from '../../api/temp-log'
import { dateFromDayKey } from './temp-log-days'
import { type Mealtime, MEALTIME_LABEL, type Temperature } from './types'

// ---------------------------------------------------------------------------
// Context shape the page renders
// ---------------------------------------------------------------------------

export interface TempLogDeliverable {
  id: string
  name: string
  /** Pre-formatted quantity, e.g. "420x". */
  qtyLabel: string
  /**
   * HACCP class — drives the checkpoint presets and the expected-temp
   * prefill (hot → Hot, chilled → Chilled, ambient → Neutral). `null`
   * means the server could not derive a class: no prefill, manual reading.
   */
  temperature: Temperature | null
  /** Previously-confirmed Kitchen °F, read back from the server. */
  savedKitchenF: number | null
  /** Previously-confirmed Drop-off °F, read back from the server. */
  savedDropOffF: number | null
}

export interface TempLogMealtimeGroup {
  mealtime: Mealtime
  label: string
  /** Sum of deliverable quantities — the "· N Meals" count. */
  mealCount: number
  deliverables: TempLogDeliverable[]
}

export interface TempLogLocation {
  id: string
  name: string
  organizationName: string
  /** Single-line address. */
  address: string
  /** "Drop-off between 9:45 AM and 10:15 AM". */
  deliveryWindow: string
  /** True when the server has every task at this location COMPLETED. */
  confirmed: boolean
  mealtimes: TempLogMealtimeGroup[]
}

export interface TempLogDayContext {
  /** e.g. "Monday, June 1" — shown in the workflow subtitle. */
  serviceDateLabel: string
  /** Every location the day's task covers, in delivery order. */
  locations: TempLogLocation[]
}

// ---------------------------------------------------------------------------
// Server → view projection (pure, exported for tests)
// ---------------------------------------------------------------------------

/** Server mealtime enum → the client's lowercase `Mealtime` axis. */
const SERVER_MEALTIME_TO_CLIENT: Record<string, Mealtime> = {
  BREAKFAST: 'breakfast',
  LUNCH: 'lunch',
  SNACK: 'pm-snack',
  SUPPER: 'supper',
}

/**
 * AMBIENT passes through: shelf-stable rows offer the "Neutral" choice
 * (no reading required — matching the cafeteria wizards' room-temp
 * exemption) instead of degrading to a chilled reading prompt. `unknown`
 * (no derivable class) maps to null: no prefill, manual reading required —
 * unknown is not shelf-stable.
 */
function wireTemperatureToClient(t: 'hot' | 'chilled' | 'ambient' | 'unknown'): Temperature | null {
  if (t === 'hot' || t === 'chilled' || t === 'ambient') {
    return t
  }
  return null
}

export function projectWireLocation(wire: TempLogContextLocationWire): TempLogLocation {
  return {
    id: wire.id,
    name: wire.name,
    organizationName: wire.organizationName,
    address: wire.address,
    deliveryWindow: wire.deliveryWindow,
    confirmed: wire.confirmed,
    mealtimes: wire.mealtimes.map((group) => {
      const mealtime = SERVER_MEALTIME_TO_CLIENT[group.mealtime] ?? 'lunch'
      return {
        mealtime,
        label: MEALTIME_LABEL[mealtime] ?? group.label,
        mealCount: group.mealCount,
        deliverables: group.deliverables.map((d) => ({
          id: d.id,
          name: d.name,
          qtyLabel: `${d.plannedQty}x`,
          temperature: wireTemperatureToClient(d.temperature),
          savedKitchenF: d.savedKitchenF,
          savedDropOffF: d.savedDropOffF,
        })),
      }
    }),
  }
}

export function serviceDateLabelForDayKey(dayKey: string): string {
  const d = dateFromDayKey(dayKey)
  if (!d) {
    return 'today'
  }
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
}

/** Just the weekday — "Monday" — for the day task's title. */
export function weekdayLabelForDayKey(dayKey: string): string {
  const d = dateFromDayKey(dayKey)
  if (!d) {
    return 'today'
  }
  return d.toLocaleDateString(undefined, { weekday: 'long' })
}

/**
 * Synthesize the inbox row for one open temp-log day. The id keeps the
 * `temp-log-day-<dayKey>` shape so `workflowRouteForTask` and the
 * day-key helpers keep working; `dueAt` comes from the server's
 * generator policy (end of school week) rather than end-of-day.
 */
export function buildChefTempLogDayTask(day: TempLogDaySummaryWire): MyTaskRow {
  return {
    id: `temp-log-day-${day.dayKey}`,
    organizationId: '',
    category: 'OPERATIONS',
    subKind: 'CHEF_SUBMIT_TEMP_LOG',
    title: `Submit temperature logs for ${weekdayLabelForDayKey(day.dayKey)}`,
    description: 'Log the temperature of each item at the kitchen and at drop-off, for every delivery location.',
    blocksAction: 'payment.issue',
    blocksSubjectKind: 'SHIPMENT',
    blocksSubjectId: null,
    status: 'OPEN',
    dueAt: day.dueAt,
    createdAt: day.dueAt ?? `${day.dayKey}T00:00:00.000Z`,
    assignedToProfileCode: 'CHEF_STAFF',
    taskerTaskId: null,
    organization: { id: '', name: '' },
  }
}

// ---------------------------------------------------------------------------
// Hooks — the swap-in points for the tasks page + workflow page
// ---------------------------------------------------------------------------

export interface ChefTempLogTasksResult {
  /** One synthetic `MyTaskRow` per open day, ascending by day. */
  tasks: MyTaskRow[]
  isPending: boolean
  error: Error | null
}

/**
 * The chef's open temperature-log tasks — ONE per open day, each
 * covering every location with deliverables. Grouping happens
 * server-side from the real per-shipment tasks, so progress follows
 * the chef across devices. Pass `enabled: false` for non-chef personas
 * to skip the fetch entirely.
 */
export function useChefTempLogTasks(enabled = true): ChefTempLogTasksResult {
  const query = useTempLogDaysQuery(enabled)
  const tasks = useMemo(() => (query.data?.days ?? []).map(buildChefTempLogDayTask), [query.data])
  return {
    tasks,
    isPending: enabled && query.isPending,
    error: query.error ?? null,
  }
}

export interface TempLogDayContextResult {
  /** Null while loading / on error / when the day has no locations. */
  context: TempLogDayContext | null
  isPending: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Resolve every location a day's temp-log task covers, each with
 * deliverables grouped by meal period. `dayKey` picks which day the
 * workflow is for (an overdue Monday keeps Monday's date label).
 * Includes confirmed locations (with their server-saved readings) so
 * reopening a finished step shows what was submitted.
 */
export function useTempLogDayContext(dayKey: string): TempLogDayContextResult {
  const query = useTempLogDayContextQuery(dayKey)
  const context = useMemo(() => {
    const locations = (query.data?.locations ?? [])
      .map(projectWireLocation)
      .filter((location) => location.mealtimes.length > 0)
    if (locations.length === 0) {
      return null
    }
    return { serviceDateLabel: serviceDateLabelForDayKey(dayKey), locations }
  }, [query.data, dayKey])
  return { context, isPending: query.isPending, error: query.error ?? null, refetch: () => void query.refetch() }
}

// ---------------------------------------------------------------------------
// Photo extraction — REAL server call (Claude vision)
//
// The chef uploads a picture of the printed sheet with temps filled in
// by hand (`POST /api/temp-log/extract-photo`); the server reads it
// with Claude vision and returns per-deliverable readings the page
// pre-fills the grid with. Each value is flagged for the chef to review
// before they confirm — nothing is auto-trusted. The image is streamed
// through the request and not persisted.
// ---------------------------------------------------------------------------

/** A model-read value for one checkpoint, with how sure the model was. */
export interface ExtractedCheckpoint {
  /** °F, or null when the cell was blank / illegible. */
  value: number | null
  confidence: 'high' | 'low'
}

/** One deliverable's two extracted checkpoint readings. */
export interface ExtractedDeliverableReading {
  deliverableId: string
  kitchen: ExtractedCheckpoint
  dropOff: ExtractedCheckpoint
}

/**
 * The server's verdict on whether the photo is actually this location's
 * sheet for this service date. Mirrors the server's `SheetVerification`.
 * Fail-open: only an explicit `matchesExpected === false` is a mismatch.
 */
export interface SheetVerification {
  matchesExpected: boolean
  reason: string
  observedDateText: string | null
  observedLocationText: string | null
}

/** Readings plus the sheet-match verdict, returned by `extractTempLogFromPhoto`. */
export interface ExtractTempLogResult {
  readings: ExtractedDeliverableReading[]
  verification: SheetVerification
}

export interface ExtractTempLogInput {
  location: TempLogLocation
  serviceDateLabel: string
  file: File
}

/** Server response row — snake of the REST contract in `routes/temp-log.ts`. */
interface ExtractPhotoResponseRow {
  deliverableId: string
  kitchenTemperatureF: number | null
  kitchenConfidence: 'high' | 'low'
  dropOffTemperatureF: number | null
  dropOffConfidence: 'high' | 'low'
}

/** Full server response for the extract-photo endpoint. */
interface ExtractPhotoResponse {
  readings: ExtractPhotoResponseRow[]
  verification: SheetVerification
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const

/** Read a File into the `{ mediaType, dataBase64 }` shape the endpoint wants. */
async function fileToImagePayload(file: File): Promise<{ mediaType: string; dataBase64: string }> {
  const mediaType = file.type
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(mediaType)) {
    throw new Error('Unsupported image type. Use a JPEG, PNG, or WebP photo.')
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'))
    reader.readAsDataURL(file)
  })
  // Strip the `data:<type>;base64,` prefix — the server wants raw base64.
  const comma = dataUrl.indexOf(',')
  return { mediaType, dataBase64: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl }
}

/**
 * Send a photo of one location's printed temp sheet to the server and
 * get back per-deliverable Kitchen / Drop-off readings plus a verdict on
 * whether the photo is actually this location's sheet for this date. The
 * expected rows are derived from the location the chef is on, so the
 * model maps handwriting onto a known table. Throws on network / server
 * error so the caller can surface a retry + manual-entry fallback.
 */
export async function extractTempLogFromPhoto(input: ExtractTempLogInput): Promise<ExtractTempLogResult> {
  const image = await fileToImagePayload(input.file)
  const rows = input.location.mealtimes.flatMap((group) =>
    group.deliverables.map((d) => ({
      deliverableId: d.id,
      name: d.name,
      mealtimeLabel: group.label,
      temperatureClass: d.temperature,
    })),
  )

  const result = await apiFetch<ExtractPhotoResponse>('/api/temp-log/extract-photo', {
    method: 'POST',
    body: {
      locationName: input.location.name,
      serviceDateLabel: input.serviceDateLabel,
      image,
      rows,
    },
  })

  return {
    readings: result.readings.map((r) => ({
      deliverableId: r.deliverableId,
      kitchen: { value: r.kitchenTemperatureF, confidence: r.kitchenConfidence },
      dropOff: { value: r.dropOffTemperatureF, confidence: r.dropOffConfidence },
    })),
    verification: result.verification,
  }
}
